import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getDatabase, onValue, ref } from 'firebase/database';

import useStore from '@hooks/useStore';
import useAccountsPayable from '@hooks/useAccountsPayable';
import { AccountsPayableRouteParams } from '@containers/types';
import { TxnFirebaseUpdate } from '@store/accountsPayableLink/models/AccountsPayable';

export interface IUseAccountsPayableFireBase {
  autoRefresh?: boolean;
}

const FINAL_BATCH_STATUSES = [
  'DISPERSION_SUCCESS',
  'PARTIALLY_DISPERSED',
  'DISPERSION_FAIL',
  'DISPERSION_ERROR',
];

function useAccountsPayableFireBase({ autoRefresh = true }): IUseAccountsPayableFireBase | null {
  const { accountsPayableLink } = useStore();
  const { handleError } = useAccountsPayable();
  const params = useParams<AccountsPayableRouteParams>();
  const { getAccountsPayableLink } = accountsPayableLink;
  const accountsPayableLinkId = params.accountsPayableLinkId;

  const fetchData = async (silent = false) => {
    try {
      await getAccountsPayableLink(accountsPayableLinkId, silent);
    } catch (error) {
      handleError(error as { message: string });
    }
  };

  useEffect(() => {
    const db = getDatabase();
    const batchRef = ref(db, 'batches/' + accountsPayableLinkId);

    const unsubscribe = onValue(batchRef, (snapshot) => {
      const data = snapshot.val();

      // Nodo borrado (deleteFbDatabasePath al completar el batch) → re-fetch final como fallback
      if (!data) {
        if (autoRefresh) fetchData(true);
        return;
      }

      // Si hay sub-nodo txn → son deltas de transacciones individuales, aplicar al store en vivo
      if (data.txn) {
        const txnUpdates = data.txn as Record<string, TxnFirebaseUpdate>;
        accountsPayableLink.applyFirebaseTxnUpdates(txnUpdates);

        // Si el batchStatus de algún delta indica que el batch terminó → re-fetch al API
        // para obtener el estado final oficial y transicionar a la pantalla de resultados
        if (autoRefresh) {
          const batchFinished = Object.values(txnUpdates).some((u) =>
            FINAL_BATCH_STATUSES.includes(u.batchStatus),
          );
          if (batchFinished) fetchData(true);
        }
        return;
      }

      // Nodo raíz sin txn → cambio de fondeo inicial, hacer re-fetch al API para
      // cargar todos los datos del batch y pasar a la pantalla de procesamiento
      if (autoRefresh) fetchData(true);
    });

    return () => unsubscribe();
  }, []);

  return null;
}
export default useAccountsPayableFireBase;
